/**
 * The caller's own team memberships.
 *
 * Its own module, small and dependency-light on purpose. The catalog adapter
 * needs it to stamp team ids on an auto-attach, and that adapter sits in the
 * app's entry chunk — importing it from `useTeamRoster` would pull the
 * collaboration and streaming clients in behind it for every page load.
 */
import * as React from "react";
import { useSession } from "@authe/ui-core/session";
import { useTeamsApi } from "@/api/adapters";

/** A team the caller belongs to, as a share target names it. */
export interface MyTeam {
  id: string;
  name: string;
}

/**
 * Distinct from `useTeamIds` in `useTeamRoster`, which is gated on a mounted
 * streaming client because channel subscription is all it is for. Sharing is
 * not: an attach scoped to a team needs those ids on a page with no live
 * transport, and gating them there means a "team" write that reaches nobody.
 */
export function useMyTeams(): MyTeam[] {
  const { session } = useSession();
  const teamsApi = useTeamsApi();
  const userId = session?.user.id ?? null;

  const [teams, setTeams] = React.useState<MyTeam[]>([]);
  React.useEffect(() => {
    if (!userId || !teamsApi.listMemberships) {
      setTeams([]);
      return undefined;
    }
    let alive = true;
    void teamsApi
      .listMemberships(userId)
      .then((ms) => {
        if (!alive) return;
        setTeams(
          ms
            .filter((m) => Boolean(m.team.id))
            .map((m) => ({ id: m.team.id, name: m.team.name || m.team.id })),
        );
      })
      .catch(() => {
        if (alive) setTeams([]);
      });
    return () => {
      alive = false;
    };
  }, [teamsApi, userId]);

  return teams;
}
